import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useAccount, useBalance, useReadContracts } from "wagmi";
import { formatUnits, formatEther } from "viem";
import { ExternalLink, UserRound } from "lucide-react";
import { erc20Abi } from "../lib/abis";
import { baseTokens, NATIVE } from "../lib/swap";
import { useTokenList } from "../components/TokenSelect";
import { fmtNum, short } from "../lib/format";
import { addrUrl } from "../lib/chains";
import { useApp } from "../context/AppContext";
import { PageHead } from "./Explore";
import { TokenLogo } from "../components/TokenSelect";
import { CreatorEarnings } from "../components/CreatorEarnings";
import { TokenVisibility } from "../components/TokenVisibility";
import { Panel, Empty, Loading } from "../components/State";

const Balances = ({ address }) => {
  const { config } = useApp();
  const tokens = useTokenList(useMemo(() => baseTokens(config), [config]));
  const erc = tokens.filter((t) => t.address !== NATIVE);
  const eth = useBalance({ address, chainId: 4663 });
  const ethT = useBalance({ address, chainId: 46630 });
  const { data, isLoading } = useReadContracts({ contracts: erc.map((t) => ({ address: t.address, abi: erc20Abi, functionName: "balanceOf", args: [address], chainId: 4663 })) });
  const rows = [
    { symbol: "ETH", name: "Robinhood Chain mainnet", v: eth.data ? formatEther(eth.data.value) : null },
    { symbol: "ETH", name: "Robinhood Chain testnet", v: ethT.data ? formatEther(ethT.data.value) : null },
    ...erc.map((t, i) => ({ symbol: t.symbol, name: t.name, address: t.address, v: data?.[i]?.result !== undefined ? formatUnits(data[i].result, t.decimals) : null })),
  ];
  if (isLoading) return <Loading />;
  return (
    <table className="w-full text-sm" data-testid="portfolio-balances">
      <tbody>{rows.map((r, i) => (
        <tr key={i} className="border-b border-white/5">
          <td className="px-5 py-3 font-semibold text-white"><span className="flex items-center gap-2"><TokenLogo t={{ symbol: r.symbol, address: r.address || "0x0Bd7D308f8E1639FAb988df18A8011f41EAcAD73" }} size={22} />{r.address ? <Link to={`/token/${r.address}`} className="hover:text-lime">{r.symbol}</Link> : r.symbol}</span></td>
          <td className="px-5 py-3 text-xs text-slate-500">{r.name}</td>
          <td className="px-5 py-3 text-right font-mono" data-testid={`portfolio-balance-${i}`}>{r.v === null ? "—" : fmtNum(r.v, 6)}</td>
        </tr>))}
      </tbody>
    </table>
  );
};

const Profile = () => {
  const { user, signIn } = useApp();
  if (!user) return <div className="p-5"><button data-testid="portfolio-sign-in" onClick={signIn} className="btn-lime px-4 py-2 text-xs">Sign in with Ethereum</button></div>;
  return (
    <div className="p-5 flex items-center gap-3 text-xs text-slate-400">
      Signed in as <span className="font-mono text-slate-200">{user.alias || short(user.address, 6)}</span> · role: {user.role}
      <Link to={`/u/${user.address}`} data-testid="portfolio-open-profile" className="ml-auto text-lime hover:underline">View & edit profile →</Link>
    </div>
  );
};

const SignedInVisibility = () => {
  const { user } = useApp();
  return user ? <TokenVisibility key={user.address} /> : null;
};

export default function Portfolio() {
  const { address, isConnected } = useAccount();
  return (
    <div className="mx-auto max-w-[1100px] px-4 lg:px-8 py-10 space-y-6" data-testid="portfolio-page">
      <PageHead kicker="Portfolio" title="Your hive">Live balances read directly from Robinhood Chain RPC.</PageHead>
      {!isConnected ? <Panel><Empty testid="portfolio-connect" title="Connect a wallet to view balances" /></Panel> : (<>
        <Panel title="Profile" icon={UserRound}><Profile /></Panel>
        <CreatorEarnings wallet={address} isMe />
        <SignedInVisibility />
        <Panel title="Balances" right={<a href={addrUrl(address)} target="_blank" rel="noreferrer" className="text-xs text-lime flex items-center gap-1">Full history on Blockscout <ExternalLink className="h-3 w-3" /></a>}>
          <Balances address={address} />
        </Panel>
      </>)}
    </div>
  );
}
